'use client';

import { RadialBarChart, RadialBar, PolarAngleAxis, Cell } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '../chart';

export interface RadialStackedItem {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface RadialStackedChartProps {
  data: RadialStackedItem[];
  showLegend?: boolean;
  height?: number;
  className?: string;
}

export function RadialStackedChart({
  data,
  showLegend = true,
  height = 220,
  className,
}: RadialStackedChartProps) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d) => [d.key, { label: d.label, color: d.color }]),
  );

  const stackedData = data.map((d) => ({
    ...d,
    fill: `var(--color-${d.key})`,
  }));

  return (
    <ChartContainer config={config} className={className} style={{ height }}>
      {/* Concentric rings: recharts v3 lays each data row out as its own ring
          between the chart's inner/outer radius. Per-ring radius props on
          <RadialBar> aren't valid in v3 — the chart owns the radius, and each
          ring is coloured via <Cell>. */}
      <RadialBarChart
        data={stackedData}
        startAngle={90}
        endAngle={-270}
        innerRadius="20%"
        outerRadius="90%"
      >
        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
        <RadialBar dataKey="value" background cornerRadius={4}>
          {stackedData.map((entry) => (
            <Cell key={entry.key} fill={entry.fill} />
          ))}
        </RadialBar>
        <ChartTooltip content={<ChartTooltipContent />} />
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </RadialBarChart>
    </ChartContainer>
  );
}
